import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DoctorInvitationService } from '../../core/services/doctor-invitation.service';
import { NotificationService } from '../../core/services/notification.service';
import { DoctorSearchResponse } from '../../core/models/doctor-search-response.model';
import { PendingInvitationResponse } from '../../core/models/pending-invitation-response.model';

@Component({
  selector: 'app-invite-clinic-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './invite-clinic-user.html',
  styleUrl: './invite-clinic-user.scss',
})
export class InviteClinicUser implements OnInit {
  searchForm: FormGroup;
  doctorResult: DoctorSearchResponse | null = null;
  clinicId: string | null = null;

  isSearching = false;
  isInviting = false;
  searchAttempted = false;
  errorMessage: string | null = null;

  pendingInvitations: PendingInvitationResponse[] = [];
  isLoadingPending = false;
  activeDoctorCount = 0;

  constructor(
    private fb: FormBuilder,
    private invitationService: DoctorInvitationService,
    private notification: NotificationService,
  ) {
    this.searchForm = this.fb.group({
      invitationCode: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  ngOnInit(): void {
    this.clinicId = sessionStorage.getItem('clinicId');
    if (!this.clinicId) {
      const msg = 'Clinic ID not found. Please log in again.';
      this.errorMessage = msg;
      this.notification.error(msg, 'Session Error');
    } else {
      this.loadPendingInvitations();
      this.calculateActiveDoctorCount();
    }
  }

  private calculateActiveDoctorCount(): void {
    // Count based on pending invitations (invited doctors) and this represents active doctors
    this.activeDoctorCount = this.pendingInvitations.length;
  }

  get doctorCapReached(): boolean {
    return this.activeDoctorCount >= 3;
  }

  loadPendingInvitations(): void {
    if (!this.clinicId) return;
    this.isLoadingPending = true;
    this.invitationService.getPendingInvitations(this.clinicId).subscribe({
      next: (res) => {
        this.pendingInvitations = res;
        this.calculateActiveDoctorCount();
        this.isLoadingPending = false;
      },
      error: () => {
        this.isLoadingPending = false;
      },
    });
  }

  search(): void {
    this.errorMessage = null;
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }
    this.isSearching = true;
    this.doctorResult = null;
    this.searchAttempted = true;

    const code: string = this.searchForm.value.invitationCode;
    this.invitationService.searchDoctorByCode(code).subscribe({
      next: (doctor) => {
        this.doctorResult = doctor;
        this.isSearching = false;
      },
      error: (err) => {
        this.isSearching = false;
        const msg = err?.error?.message ?? 'No doctor found with that invitation code.';
        this.errorMessage = msg;
        this.notification.error(msg, 'Search Failed');
      },
    });
  }

  invite(): void {
    this.errorMessage = null;
    if (!this.doctorResult || !this.clinicId) {
      const msg = 'Missing clinic or doctor information.';
      this.errorMessage = msg;
      this.notification.error(msg, 'Validation Error');
      return;
    }
    this.isInviting = true;
    this.invitationService
      .inviteDoctor({
        clinicId: this.clinicId,
        doctorId: this.doctorResult.doctorId,
      })
      .subscribe({
        next: () => {
          this.isInviting = false;
          this.notification.success('Doctor invited successfully!', 'Invitation Sent');
          this.doctorResult = null;
          this.searchAttempted = false;
          this.searchForm.reset();
          this.loadPendingInvitations();
        },
        error: (err) => {
          this.isInviting = false;
          const msg = err?.error?.message ?? 'Failed to send invitation. Please try again.';
          this.errorMessage = msg;
          this.notification.error(msg, 'Invitation Failed');
        },
      });
  }

  clearSearch(): void {
    this.doctorResult = null;
    this.searchAttempted = false;
    this.errorMessage = null;
    this.searchForm.reset();
  }
}
